import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class CandidateCompanyLogoQuery {
  @IsString()
  @IsOptional()
  invite?: string;
}

export class CandidateEventTypeCreateOptionsQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export type Phone = string;

export class CandidateItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsNotEmpty()
  last_name: string;

  @IsString()
  @IsOptional()
  middle_name?: string;

  @IsString()
  @IsNotEmpty()
  phone: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  email?: string;
}

export class CandidateSimilarResponse {
  @ValidateNested({ each: true })
  @Type(() => CandidateItem)
  @IsArray()
  @IsNotEmpty()
  candidates: CandidateItem[];
}

export class CandidateCreateEventResponse {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class CandidateEventTypeItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  custom_type: string;
}

export class CandidateCreateEventTypeOptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => CandidateEventTypeItem)
  @IsArray()
  @IsNotEmpty()
  event_types: CandidateEventTypeItem[];
}

export type CryptoProPluginOpenType = string;

export class CompanyItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  tsp_url?: string;

  @IsIn(['capi', 'cades'])
  @IsString()
  @IsOptional()
  crypto_pro_plugin_open_type?: 'capi' | 'cades';

  @IsIn(['api', 'browser_plugin'])
  @IsString()
  @IsOptional()
  hash_source?: 'api' | 'browser_plugin';
}

export class CandidateCreateEventTypeCompaniesResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyItem)
  @IsArray()
  @IsNotEmpty()
  companies: CompanyItem[];
}

export class CandidateResponse {
  @ValidateNested()
  @Type(() => CandidateItem)
  @IsNotEmpty()
  candidate: CandidateItem;
}

export type AccessToken = string;

export class CandidateLoginResponse {
  @IsString()
  @IsNotEmpty()
  access_token: string;
}

export class CreateCandidate {
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsNotEmpty()
  last_name: string;

  @IsString()
  @IsOptional()
  middle_name?: string;

  @IsString()
  @IsNotEmpty()
  phone: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  email?: string;
}

export class CandidateSimilarRequest {
  @ValidateNested()
  @Type(() => CreateCandidate)
  @IsNotEmpty()
  candidate: CreateCandidate;
}

export class CandidateCreateEventRequest {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;

  @ValidateNested()
  @Type(() => CreateCandidate)
  @IsNotEmpty()
  candidate: CreateCandidate;
}

export type PhoneCode = string;

export class CandidateLoginRequest {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsString()
  @IsNotEmpty()
  phone: string;

  @IsString()
  @IsNotEmpty()
  code: string;
}

export class CandidateLoginInitRequest {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsString()
  @IsNotEmpty()
  phone: string;
}

@injectable()
export class CandidatesService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async candidateCompanyLogo({
    query = {},
  }: {
    query?: CandidateCompanyLogoQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, CandidateCompanyLogoQuery),
    ]);

    let requestPath = '/candidate/company/logo';

    const result = await this.http.getFile(
      requestPath,
      {
        withAuth: false,
        withSide: false,
        isJson: true,
      },
      query,
    );

    return result;
  }

  async candidateSimilar({ body }: { body: CandidateSimilarRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CandidateSimilarRequest),
    ]);

    let requestPath = '/candidate/similar';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      CandidateSimilarResponse,
    );

    return response;
  }

  async candidateEvent({ body }: { body: CandidateCreateEventRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CandidateCreateEventRequest),
    ]);

    let requestPath = '/candidate/event';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      CandidateCreateEventResponse,
    );

    return response;
  }

  async candidateEventTypeCreateOptions({
    query,
  }: {
    query: CandidateEventTypeCreateOptionsQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        CandidateEventTypeCreateOptionsQuery,
      ),
    ]);

    let requestPath = '/candidate/event_type/create_options';

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      CandidateCreateEventTypeOptionsResponse,
    );

    return response;
  }

  async candidateEventTypeCreateCompanies() {
    let requestPath = '/candidate/event_type/create_companies';

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      CandidateCreateEventTypeCompaniesResponse,
    );

    return response;
  }

  async candidate() {
    let requestPath = '/candidate';

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      CandidateResponse,
    );

    return response;
  }

  async candidateLogin({ body }: { body: CandidateLoginRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CandidateLoginRequest),
    ]);

    let requestPath = '/candidate/login';

    const result = await this.http.post(requestPath, body, { withAuth: false });

    const response = await this.unmarshaller.unmarshall(
      result,
      CandidateLoginResponse,
    );

    return response;
  }

  async candidateLoginInit({ body }: { body: CandidateLoginInitRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CandidateLoginInitRequest),
    ]);

    let requestPath = '/candidate/login/init';

    const result = await this.http.post(requestPath, body, { withAuth: false });

    return result;
  }
}
